import React, { useState } from "react";
import { updatePlatformSettings, runMarginCheck, formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { BellRing, Loader2, Save } from "lucide-react";

export const MarginAlertSettings = ({ settings, overCount, onChanged }) => {
  const [pct, setPct] = useState(settings.margin_threshold_pct);
  const [ceiling, setCeiling] = useState(settings.trial_ceiling_gbp);
  const [busy, setBusy] = useState(null);

  const save = async () => {
    setBusy("save");
    try {
      await updatePlatformSettings({ margin_threshold_pct: Number(pct), trial_ceiling_gbp: Number(ceiling) });
      toast.success("Margin alert thresholds saved");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } finally { setBusy(null); }
  };

  const run = async () => {
    setBusy("run");
    try {
      const r = await runMarginCheck();
      toast.success(r.skipped ? `Check skipped: ${r.skipped}` : r.sent ? `Sent ${r.sent} alert${r.sent === 1 ? "" : "s"}` : r.over.length ? `${r.over.length} over the line — already alerted this month` : "All workspaces within their AI budget");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } finally { setBusy(null); }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4 flex flex-wrap items-end gap-4" data-testid="margin-alert-settings">
      <div className="flex items-start gap-3 mr-auto">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-500/10 text-amber-500"><BellRing className="h-4 w-4" /></span>
        <div>
          <div className="text-sm font-medium">Margin alerts</div>
          <div className="text-xs text-muted-foreground max-w-md">Checked daily. You get one email per workspace per month when its AI cost passes the limit. {overCount ? <span className="text-amber-500 font-medium" data-testid="margin-over-count">{overCount} over the line right now.</span> : "Nobody is over the line right now."}</div>
        </div>
      </div>
      <label className="text-xs text-muted-foreground">Paid plans: alert above
        <div className="flex items-center gap-1 mt-1"><Input data-testid="margin-threshold-input" type="number" min={1} max={100} value={pct} onChange={(e) => setPct(e.target.value)} className="h-8 w-20 font-mono" /><span className="text-sm">% of plan price</span></div>
      </label>
      <label className="text-xs text-muted-foreground">Trials: alert above
        <div className="flex items-center gap-1 mt-1"><span className="text-sm">£</span><Input data-testid="trial-ceiling-input" type="number" min={0} step="0.5" value={ceiling} onChange={(e) => setCeiling(e.target.value)} className="h-8 w-24 font-mono" /><span className="text-sm">/ month</span></div>
      </label>
      <Button size="sm" variant="outline" onClick={save} disabled={busy === "save"} data-testid="margin-save-btn">{busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save</Button>
      <Button size="sm" onClick={run} disabled={busy === "run"} data-testid="margin-run-btn">{busy === "run" ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4 mr-1.5" />} Run check now</Button>
    </div>
  );
};
